import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Grid2 as Grid, TextField,
  MenuItem, Chip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Stack, Alert, CircularProgress, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  InputAdornment, Select, FormControl, InputLabel,
} from '@mui/material';
import {
  Add, Refresh, Visibility, Search, Build,
  People, CheckCircle, TrendingUp, Receipt,
  MoreVert, PlayArrow, Pause, Block, DeleteForever,
} from '@mui/icons-material';
import Menu from '@mui/material/Menu';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';

interface Agente {
  ID: string;
  CODIGO: string;
  NOME: string;
  NIF: string;
  TIPO: 'PESSOA' | 'ENTIDADE';
  TELEFONE: string;
  EMAIL: string;
  ILHA: string;
  MUNICIPIO: string;
  ESTADO: 'PENDENTE' | 'ATIVO' | 'SUSPENSO' | 'INATIVO';
  DT_REGISTO: string;
  contratoId: string | null;
  /** Float do posto. Null quando o agente ainda não tem carteira. */
  floatSaldo: number | null;
  floatMinimo: number | null;
  floatUtilizavel: number | null;
  floatEstado: string | null;
}

interface AgenteStats {
  totalAgentes: number;
  ativos: number;
  volumeMes: number;
  comissoesMes: number;
}

const ESTADO_COLOR: Record<string, 'default' | 'warning' | 'success' | 'error' | 'info'> = {
  PENDENTE: 'warning',
  ATIVO: 'success',
  SUSPENSO: 'error',
  INATIVO: 'default',
};

const ILHAS = ['Santiago', 'São Vicente', 'Santo Antão', 'Sal', 'Fogo', 'Boavista', 'Maio', 'Brava', 'São Nicolau', 'Santa Luzia'];

const emptyForm = {
  nome: '', nif: '', tipo: 'PESSOA', email: '', telefone: '',
  morada: '', ilha: '', municipio: '', localidade: '', iban: '',
};

export default function Agentes() {
  const navigate = useNavigate();
  const [agentes, setAgentes] = useState<Agente[]>([]);
  const [stats, setStats] = useState<AgenteStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [migrateMsg, setMigrateMsg] = useState<string | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuAgente, setMenuAgente] = useState<Agente | null>(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchAgentes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (estadoFilter) params.estado = estadoFilter;
      if (search) params.search = search;
      const [agentesRes, statsRes] = await Promise.all([
        api.get('/admin/agentes', { params }),
        api.get('/admin/agentes/stats').catch(() => null),
      ]);
      setAgentes(agentesRes.data?.data || []);
      if (statsRes) {
        const s = statsRes.data?.data;
        setStats({
          totalAgentes: s?.agentes?.totalAgentes || 0,
          ativos: s?.agentes?.ativos || 0,
          volumeMes: s?.transacoes?.volumeMes || 0,
          comissoesMes: s?.transacoes?.comissoesMes || 0,
        });
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar agentes');
    } finally {
      setLoading(false);
    }
  }, [estadoFilter, search]);

  useEffect(() => { fetchAgentes(); }, [fetchAgentes]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      await api.post('/admin/agentes', form);
      setDialogOpen(false);
      setForm({ ...emptyForm });
      fetchAgentes();
    } catch (err: any) {
      const d = err.response?.data;
      setError(d?.detail || d?.error || err.message || 'Erro ao criar agente');
    } finally {
      setCreating(false);
    }
  };

  const handleChangeEstado = async (agente: Agente, estado: string) => {
    setMenuAnchor(null);
    try {
      await api.put(`/admin/agentes/${agente.ID}/estado`, { estado });
      fetchAgentes();
    } catch (err: any) {
      setError(err.message || 'Erro ao alterar estado');
    }
  };

  const handleMigrate = async () => {
    try {
      const res = await api.post('/admin/agentes/migrate');
      setMigrateMsg(res.data?.message || 'Migração concluída.');
    } catch (err: any) {
      setMigrateMsg('Erro: ' + (err.message || 'falha na migração'));
    }
  };

  const handleDelete = async () => {
    if (!menuAgente) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/agentes/${menuAgente.ID}`);
      setDeleteDialog(false);
      setMenuAgente(null);
      fetchAgentes();
    } catch (err: any) {
      const d = err.response?.data;
      setError(d?.error || err.message || 'Erro ao eliminar agente');
    } finally {
      setDeleting(false);
    }
  };

  const fmt = (n: number) =>
    new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE', maximumFractionDigits: 0 }).format(n);

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde', day: '2-digit', month: '2-digit', year: 'numeric' });

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={600}>Agentes Pagali</Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Criar tabelas na BD (primeira vez)">
            <Button variant="outlined" size="small" startIcon={<Build />} onClick={handleMigrate}>
              Migrar BD
            </Button>
          </Tooltip>
          <Button variant="contained" startIcon={<Add />} onClick={() => setDialogOpen(true)}>
            Novo Agente
          </Button>
        </Stack>
      </Stack>

      {migrateMsg && (
        <Alert severity={migrateMsg.startsWith('Erro') ? 'error' : 'success'} onClose={() => setMigrateMsg(null)} sx={{ mb: 2 }}>
          {migrateMsg}
        </Alert>
      )}
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}

      {/* Stats */}
      {stats && (
        <Grid container spacing={2} mb={3}>
          {[
            { label: 'Total Agentes', value: stats.totalAgentes, icon: <People color="primary" />, color: 'primary.light' },
            { label: 'Agentes Activos', value: stats.ativos, icon: <CheckCircle color="success" />, color: 'success.light' },
            { label: 'Volume Mês', value: fmt(stats.volumeMes), icon: <TrendingUp color="info" />, color: 'info.light' },
            { label: 'Comissões Mês', value: fmt(stats.comissoesMes), icon: <Receipt color="warning" />, color: 'warning.light' },
          ].map(s => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={s.label}>
              <Card variant="outlined">
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ bgcolor: s.color, borderRadius: 2, p: 1, display: 'flex' }}>{s.icon}</Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">{s.label}</Typography>
                    <Typography variant="h6" fontWeight={600}>{s.value}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Filtros */}
      <Stack direction="row" spacing={2} mb={2}>
        <TextField
          size="small" placeholder="Pesquisar nome, NIF, código..." value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && fetchAgentes()}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          sx={{ width: 280 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Estado</InputLabel>
          <Select value={estadoFilter} label="Estado" onChange={e => setEstadoFilter(e.target.value)}>
            <MenuItem value="">Todos</MenuItem>
            {['PENDENTE', 'ATIVO', 'SUSPENSO', 'INATIVO'].map(e => (
              <MenuItem key={e} value={e}>{e}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <IconButton onClick={fetchAgentes} disabled={loading}><Refresh /></IconButton>
      </Stack>

      {/* Tabela */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell><strong>Código</strong></TableCell>
                <TableCell><strong>Nome</strong></TableCell>
                <TableCell><strong>Tipo</strong></TableCell>
                <TableCell><strong>Ilha</strong></TableCell>
                <TableCell><strong>Telefone</strong></TableCell>
                <TableCell><strong>Contrato</strong></TableCell>
                <TableCell align="right"><strong>Float</strong></TableCell>
                <TableCell><strong>Estado</strong></TableCell>
                <TableCell><strong>Registo</strong></TableCell>
                <TableCell align="center"><strong>Acções</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {agentes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Nenhum agente encontrado. Crie o primeiro agente ou clique em "Migrar BD" se for a primeira vez.
                  </TableCell>
                </TableRow>
              ) : agentes.map(a => (
                <TableRow key={a.ID} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{a.CODIGO}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{a.NOME}</TableCell>
                  <TableCell>
                    <Chip label={a.TIPO} size="small" variant="outlined"
                      color={a.TIPO === 'ENTIDADE' ? 'primary' : 'default'} sx={{ fontSize: 11 }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{a.ILHA}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{a.TELEFONE}</TableCell>
                  <TableCell>
                    <Chip
                      label={a.contratoId ? 'Activo' : 'Sem contrato'}
                      size="small"
                      color={a.contratoId ? 'success' : 'default'}
                      sx={{ fontSize: 11 }}
                    />
                  </TableCell>
                  {/* Float utilizável — o que resta depois da reserva. É o número
                      que diz se este posto ainda consegue carregar carteiras. */}
                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {a.floatSaldo == null ? (
                      <Typography variant="caption" color="text.secondary">sem carteira</Typography>
                    ) : (
                      <Tooltip title={
                        `Saldo ${Number(a.floatSaldo).toLocaleString('pt-PT')} · reserva ${Number(a.floatMinimo ?? 0).toLocaleString('pt-PT')}`
                      }>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={
                            Number(a.floatUtilizavel ?? 0) <= 0 ? 'error.main'
                              : Number(a.floatUtilizavel ?? 0) < 5000 ? 'warning.main'
                                : 'text.primary'
                          }
                        >
                          {Number(a.floatUtilizavel ?? 0).toLocaleString('pt-PT')}$
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip label={a.ESTADO} size="small" color={ESTADO_COLOR[a.ESTADO]} sx={{ fontSize: 11 }} />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{formatDate(a.DT_REGISTO)}</TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Tooltip title="Ver detalhes">
                        <IconButton size="small" color="primary" onClick={() => navigate(`/management/agentes/${a.ID}`)}>
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Acções">
                        <IconButton size="small" onClick={e => { setMenuAnchor(e.currentTarget); setMenuAgente(a); }}>
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Menu: Acções do Agente */}
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        {menuAgente?.ESTADO !== 'ATIVO' && (
          <MenuItem onClick={() => handleChangeEstado(menuAgente!, 'ATIVO')}>
            <PlayArrow fontSize="small" sx={{ mr: 1, color: 'success.main' }} /> Activar
          </MenuItem>
        )}
        {menuAgente?.ESTADO === 'ATIVO' && (
          <MenuItem onClick={() => handleChangeEstado(menuAgente!, 'SUSPENSO')}>
            <Pause fontSize="small" sx={{ mr: 1, color: 'warning.main' }} /> Suspender
          </MenuItem>
        )}
        {menuAgente?.ESTADO !== 'INATIVO' && (
          <MenuItem onClick={() => handleChangeEstado(menuAgente!, 'INATIVO')}>
            <Block fontSize="small" sx={{ mr: 1, color: 'error.main' }} /> Desactivar
          </MenuItem>
        )}
        <MenuItem onClick={() => { setMenuAnchor(null); navigate(`/management/agentes/${menuAgente?.ID}`); }}>
          <Visibility fontSize="small" sx={{ mr: 1 }} /> Ver detalhes
        </MenuItem>
        <MenuItem onClick={() => { setMenuAnchor(null); setDeleteDialog(true); }} sx={{ color: 'error.main' }}>
          <DeleteForever fontSize="small" sx={{ mr: 1 }} /> Eliminar conta
        </MenuItem>
      </Menu>

      {/* Dialog: Confirmar eliminação */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteForever /> Eliminar Agente
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            Esta acção é irreversível. Todos os dados do agente serão eliminados permanentemente.
          </Alert>
          <Typography variant="body2">
            Tem a certeza que pretende eliminar o agente <strong>{menuAgente?.NOME}</strong> ({menuAgente?.CODIGO})?
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            Serão eliminados: conta de acesso, contratos, transacções, wallet e facturas de comissão.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)} disabled={deleting}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteForever />}>
            {deleting ? 'A eliminar...' : 'Eliminar definitivamente'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Criar Agente */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Novo Agente Pagali</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField fullWidth size="small" label="Nome completo *" value={form.nome}
                onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField fullWidth size="small" label="NIF" value={form.nif}
                onChange={e => setForm(f => ({ ...f, nif: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo *</InputLabel>
                <Select value={form.tipo} label="Tipo *" onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}>
                  <MenuItem value="PESSOA">Pessoa singular</MenuItem>
                  <MenuItem value="ENTIDADE">Entidade / Empresa</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Ilha *</InputLabel>
                <Select value={form.ilha} label="Ilha *" onChange={e => setForm(f => ({ ...f, ilha: e.target.value }))}>
                  {ILHAS.map(i => <MenuItem key={i} value={i}>{i}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Email *" type="email" value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Telefone *" value={form.telefone}
                onChange={e => setForm(f => ({ ...f, telefone: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Município" value={form.municipio}
                onChange={e => setForm(f => ({ ...f, municipio: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label="Localidade" value={form.localidade}
                onChange={e => setForm(f => ({ ...f, localidade: e.target.value }))} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" label="Morada" value={form.morada}
                onChange={e => setForm(f => ({ ...f, morada: e.target.value }))} />
            </Grid>
            <Grid size={12}>
              <TextField fullWidth size="small" label="IBAN (para liquidação de comissões)" value={form.iban}
                onChange={e => setForm(f => ({ ...f, iban: e.target.value }))}
                placeholder="CV60..." />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating || !form.nome || !form.email}>
            {creating ? <CircularProgress size={18} /> : 'Criar Agente'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
